import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Copy, LinkSimple, CreditCard, ArrowsClockwise, CurrencyDollar } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";

const EMPTY = { name: "", email: "", ref_code: "", commission_pct: 20, notes: "" };

function ShareLink({ refCode }) {
  const url = `${window.location.origin}/cash-health-check?ref=${refCode}`;
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
      className="font-mono text-[10px] text-zinc-500 hover:text-zinc-950 flex items-center gap-1"
      title={url}
      data-testid={`affiliate-copy-${refCode}`}
    >
      <Copy size={10} /> COPY LINK
    </button>
  );
}

function ConnectControls({ aff, onChange }) {
  const [busy, setBusy] = useState(false);
  const [payAmount, setPayAmount] = useState("");

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/affiliates/${aff.id}/connect/start`);
      // Open Stripe-hosted onboarding in a new tab
      window.open(data.onboarding_url, "_blank", "noopener,noreferrer");
      toast.success("Opened Stripe onboarding in a new tab");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Stripe Connect unavailable");
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/affiliates/${aff.id}/connect/refresh`);
      toast.success("Status refreshed");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const pay = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    if (!window.confirm(`Send $${amt.toFixed(2)} to ${aff.name} via Stripe Connect?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/affiliates/${aff.id}/payouts`, { amount_usd: amt });
      toast.success(`Paid $${amt.toFixed(2)} to ${aff.name}`);
      setPayAmount("");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!aff.stripe_account_id) {
    return (
      <button
        onClick={startOnboarding}
        disabled={busy}
        className="text-[10px] font-mono text-zinc-950 border border-zinc-300 hover:border-zinc-950 px-2 py-1 flex items-center gap-1 disabled:opacity-50"
        data-testid={`affiliate-connect-${aff.id}`}
      >
        <CreditCard size={10} /> CONNECT STRIPE
      </button>
    );
  }

  if (!aff.payouts_enabled) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-mono px-1.5 py-0.5 bg-amber-50 text-amber-700">ONBOARDING</span>
        <button onClick={startOnboarding} disabled={busy} className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950" title="Resume onboarding" data-testid={`affiliate-resume-${aff.id}`}>RESUME</button>
        <button onClick={refresh} disabled={busy} className="text-zinc-400 hover:text-zinc-950" title="Refresh status" data-testid={`affiliate-refresh-${aff.id}`}><ArrowsClockwise size={11} /></button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-50 text-emerald-700">PAYOUTS ON</span>
      <input
        type="number"
        step="0.01"
        min="0.01"
        placeholder="$"
        value={payAmount}
        onChange={(e) => setPayAmount(e.target.value)}
        className="w-16 border border-zinc-200 focus:border-zinc-950 px-1 py-0.5 text-[11px] tnum outline-none"
        data-testid={`affiliate-payout-amount-${aff.id}`}
      />
      <button
        onClick={pay}
        disabled={busy || !payAmount}
        className="text-[10px] font-mono bg-zinc-950 text-white px-2 py-0.5 hover:bg-[#FF3E00] disabled:opacity-30 flex items-center gap-1"
        data-testid={`affiliate-payout-btn-${aff.id}`}
      >
        <CurrencyDollar size={10} weight="bold" /> PAY
      </button>
    </div>
  );
}

export default function Affiliates() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/affiliates");
      setItems(data || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Admin only");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // After returning from Stripe-hosted onboarding (?connect=success), auto-refresh statuses.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("connect")) {
      const flag = params.get("connect");
      if (flag === "success") toast.success("Welcome back — syncing Stripe Connect status…");
      else if (flag === "refresh") toast("Resume the onboarding link to finish");
      // Clear the query so it doesn't fire again on next render
      window.history.replaceState({}, "", window.location.pathname);
      refresh();
    }
  }, [refresh]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/affiliates", draft);
      toast.success(`Affiliate '${draft.name}' added`);
      setDraft(EMPTY);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove affiliate '${name}'? (Leads keep their ref attribution)`)) return;
    await api.delete(`/admin/affiliates/${id}`);
    toast.success("Removed");
    refresh();
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="affiliates-page">
      <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 13 · AFFILIATES</div>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Affiliates.</h1>
      <p className="text-sm text-zinc-500 mt-2 max-w-xl">Track lead-magnet referrers. Each affiliate gets a unique <code className="bg-zinc-100 px-1 font-mono text-xs">?ref=</code> link they can share — leads are auto-attributed and commission is computed on attributed revenue.</p>

      <section className="border border-zinc-950 mt-6">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
          <Plus size={12} /> new affiliate
        </div>
        <form onSubmit={create} className="p-5 grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="affiliate-form">
          <div>
            <label className="label-sm text-zinc-500">Name</label>
            <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" data-testid="affiliate-name-input" />
          </div>
          <div>
            <label className="label-sm text-zinc-500">Email</label>
            <input required type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none font-mono" data-testid="affiliate-email-input" />
          </div>
          <div>
            <label className="label-sm text-zinc-500">Ref code</label>
            <input required value={draft.ref_code} onChange={(e) => setDraft({ ...draft, ref_code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} placeholder="joe-blog" pattern="[a-z0-9_\-]{2,32}" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none font-mono" data-testid="affiliate-refcode-input" />
          </div>
          <div>
            <label className="label-sm text-zinc-500">Commission %</label>
            <input required type="number" step="0.1" min="0" max="100" value={draft.commission_pct} onChange={(e) => setDraft({ ...draft, commission_pct: parseFloat(e.target.value) })} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none tnum" data-testid="affiliate-commission-input" />
          </div>
          <div className="flex items-end">
            <button type="submit" className="w-full bg-zinc-950 text-white text-xs font-semibold py-2.5 hover:bg-[#FF3E00]" data-testid="affiliate-save-btn">ADD</button>
          </div>
        </form>
      </section>

      <section className="mt-8">
        <div className="label-sm text-zinc-500 mb-3">Affiliates ({items.length})</div>
        <div className="border border-zinc-200">
          <div className="grid grid-cols-[1fr_140px_70px_90px_120px_110px_180px_50px] gap-px bg-zinc-200 border-b border-zinc-200">
            <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2">Affiliate</div>
            <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2">Ref Code</div>
            <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Leads</div>
            <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Avg Score</div>
            <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Revenue</div>
            <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Commission</div>
            <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2">Stripe Connect</div>
            <div className="bg-zinc-50 px-3 py-2" />
          </div>
          <div data-testid="affiliates-list">
            {items.map((a) => (
              <div key={a.id} className="grid grid-cols-[1fr_140px_70px_90px_120px_110px_180px_50px] gap-px bg-zinc-100 border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`affiliate-${a.id}`}>
                <div className="bg-white px-3 py-2.5">
                  <div className="font-medium text-sm">{a.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{a.email}</div>
                </div>
                <div className="bg-white px-3 py-2.5">
                  <div className="font-mono text-xs text-zinc-950 flex items-center gap-1.5"><LinkSimple size={11} /> {a.ref_code}</div>
                  <ShareLink refCode={a.ref_code} />
                </div>
                <div className="bg-white px-3 py-2.5 text-right tnum">{a.stats.lead_count}</div>
                <div className="bg-white px-3 py-2.5 text-right tnum text-zinc-500">{a.stats.avg_score || "—"}</div>
                <div className="bg-white px-3 py-2.5 text-right tnum">${a.stats.total_revenue.toLocaleString()}</div>
                <div className="bg-white px-3 py-2.5 text-right tnum font-semibold text-emerald-700">${a.stats.commission_usd.toLocaleString()}</div>
                <div className="bg-white px-2 py-2.5 flex items-center">
                  <ConnectControls aff={a} onChange={refresh} />
                </div>
                <div className="bg-white px-3 py-2.5 text-right">
                  <button onClick={() => remove(a.id, a.name)} className="text-zinc-400 hover:text-[#D0021B]" data-testid={`affiliate-delete-${a.id}`}><Trash size={12} /></button>
                </div>
              </div>
            ))}
            {!items.length && <div className="bg-white px-3 py-6 text-xs text-zinc-400 font-mono text-center">NO AFFILIATES YET</div>}
          </div>
        </div>
      </section>
    </div>
  );
}
